import { MeshComponent, NumberArray, Object3D } from '@wonderlandengine/api';

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

// the intrinsic size of a generated colour swatch, matching the item swatch images it stands in for.
const ITEM_SWATCH_SIZE = 90;

/**
 * Utilities:
 */

export abstract class Utilities {

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	// convert rgba colour objects to hex (convert material colours to hex used in svg).
	static rgbaToHex(rgba: Record<number, number>, forceRemoveAlpha = false) {
		let r:number, g:number, b:number;

		// handle object with 0–1 floats.
		r = rgba[0] * 255;
		g = rgba[1] * 255;
		b = rgba[2] * 255;
		const a = rgba[3];

		// convert to integers.
		r = Math.round(r);
		g = Math.round(g);
		b = Math.round(b);

		// convert to hex (pad with 0 if single digit).
		const hex = [r, g, b]
			.map((n) => n.toString(16).padStart(2, '0'))
			.join('');

		if (forceRemoveAlpha || a === undefined) {
			return `#${hex}`;
		}

		const alphaHex = Math.round(a * 255)
			.toString(16)
			.padStart(2, '0');

		return `#${hex}${alphaHex}`;
	}

	// convert a hex colour to an rgba colour of 0–1 floats (the form material colours take), or null if it cannot be parsed.
	static hexToRgba(hex: string): NumberArray | null {

		// accept '#rrggbb' and '#rrggbbaa', with or without the leading hash.
		const digits = hex.trim().replace(/^#/, '');
		if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(digits)) {
			console.error('Cannot parse hex colour: ', hex);
			return null;
		}

		// read each channel as its own pair of hex digits, scaled to a 0–1 float.
		const channel = (index: number) => parseInt(digits.slice(index * 2, (index * 2) + 2), 16) / 255;

		// return the channels, defaulting the alpha to fully opaque when the hex does not carry one.
		return [channel(0), channel(1), channel(2), digits.length === 8 ? channel(3) : 1];
	}

	// build a flat swatch image of the given hex colour as an SVG data URI, for an option whose colour is arbitrary and so has no
	// swatch file of its own. sized to match the item swatches it sits beside, though the element's own CSS is what scales it.
	static colourSwatchDataUri(hex: string): string {
		const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${ITEM_SWATCH_SIZE}" height="${ITEM_SWATCH_SIZE}">` +
			`<rect width="${ITEM_SWATCH_SIZE}" height="${ITEM_SWATCH_SIZE}" fill="${hex}"/></svg>`;

		// encode it, which is what escapes the colour's leading '#' - left raw it would be read as a fragment and the fill lost.
		return `data:image/svg+xml,${encodeURIComponent(svg)}`;
	}

	static setActiveAll(node: Object3D, isActive: boolean) {

		if (node) {

			// set the active state of the given node.
			node.active = isActive;

			// do the same for all child nodes.
			for (const child of node.children) {
				this.setActiveAll(child, isActive);
			}
		}
	}

	static getMeshComponentsFromChildren(node: Object3D, matching?: string): MeshComponent[] {
		
		// get the child objects from the given node.
		const children: Object3D[] = [];
		node.getChildren(children);

		// get the mesh component from each child.
		const meshComponents: MeshComponent[] = [];
		for (const object of children) {
			const meshComponent = object.getComponent(MeshComponent);
			if (meshComponent) {

				// if the object name contains the given string (or no string has been provided), add it to the array.
				if (!matching || object.name.includes(matching)) {
					meshComponents.push(meshComponent);
				}
			}
		}

		// return the array.
		return meshComponents;
	}

	static asCurrency(value: number, regionCode: string): string {

		// get the locale and currency for the given region.
		// reference: https://simplelocalize.io/data/locales/
		let locale: string;
		let currency: string;
		switch (regionCode.toLowerCase()) {
			case 'fr':
				locale = 'fr-FR';
				currency = 'EUR';
				break;
			case 'pt':
				locale = 'pt-PT';
				currency = 'EUR';
				break;
			case 'es':
				locale = 'es-ES';
				currency = 'EUR';
				break;
			case 'it':
				locale = 'it-IT';
				currency = 'EUR';
				break;
			case 'ch':
				locale = 'de-CH';
				currency = 'CHF';
				break;
			case 'de':
				locale = 'de-DE';
				currency = 'EUR';
				break;
			case 'be':
				locale = 'de-BE';
				currency = 'EUR';
				break;
			case 'nl':
				locale = 'nl-NL';
				currency = 'EUR';
				break;
			case 'lu':
				locale = 'de-LU';
				currency = 'EUR';
				break;
			case 'pl':
				locale = 'pl-PL';
				currency = 'PLN';
				break;
			case 'at':
				locale = 'de-AT';
				currency = 'EUR';
				break;
			case 'se':
				locale = 'sv-SE';
				currency = 'SEK';
				break;
			case 'no':
				locale = 'no-NO';
				currency = 'NOK';
				break;
			case 'jp':
				locale = 'ja-JP';
				currency = 'JPY';
				break;
			case 'au':
				locale = 'en-AU';
				currency = 'AUD';
				break;
			case 'us':
				locale = 'en-US';
				currency = 'USD';
				break;
			default:
				locale = 'en-GB';
				currency = 'GBP';
				break;
		}

		// format the number accordingly, using the correct currency and formatting.
		const formatter = new Intl.NumberFormat(locale, {
			style: 'currency',
			currency: currency
		});
		return formatter.format(value);
	}
}
